import { TablePagination } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DigitalLeadRow } from "../components/DigitalLeadRow";
import { useGetLeadsData } from "../hooks/useGetLeadsData";
import { classifyLead, HIGHLIGHT, STATUS_COLORS } from "../utils/classifyLead";
import { Modal } from "../components/Modal";
import { DownloadButton } from "../components/DownloadButton";
import { downloadExcel } from "../utils/downloadExcel";
import CustomTooltip from "../components/charts/CustomTooltip";

export default function DigitalLeadsDashboard() {
  const {
    leads,
    barData,
    statusCount,
    weekData,
    kpi,
    barDataPercent,
    testDriveBarData,
    bestSourceData,
    page,
    setPage,
    remarkModal,
    closeModal,
    editLeadRemark,
    buttonLoading,
    errors,
    handleChange,
    handleSubmit,
  } = useGetLeadsData();

  const handleExport = () => {
    // Create an array of objects with name, model, source, phone, test_drive
    const exportData = leads.map((lead) => ({
      name: lead?.name,
      model: lead?.metadata?.model,
      source: lead?.metadata?.source,
      phone: lead?.phone,
      test_drive: lead?.feedback?.test_drive,
      status: lead?.feedback ? classifyLead(lead.feedback) : "",
    }));
    downloadExcel(exportData, "nps_data");
  };
  return (
    <div className="bg-slate-900 font-montserrat min-h-screen p-6 text-white">
      <h1 className="text-3xl font-bold mb-8 tracking-tight text-white">
        Digital Leads Performance Dashboard
      </h1>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-7">
        {kpi?.map((kp) => (
          <div
            key={kp.label}
            className="bg-gradient text-center rounded-xl py-6 px-4 shadow-lg flex flex-col justify-center items-center"
            style={{
              background: "linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)",
              color: "#fff",
            }}
          >
            <span className="text-xs mb-2 tracking-wide font-semibold opacity-80">
              {kp.label}
            </span>
            <span className="text-2xl font-bold">{kp.value}</span>
          </div>
        ))}
      </div>

      {/* Stacked Bar & Status */}
      <div className="grid md:grid-cols-4 gap-7 mb-10">
        {/* Bar Chart */}
        <div className="bg-slate-800 rounded-xl p-6 col-span-2">
          <p className="font-semibold mb-4 text-white">
            Source-wise Lead Quality
          </p>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barData}>
              <XAxis dataKey="source" stroke="#fff" />
              <YAxis stroke="#fff" />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {Object.keys(STATUS_COLORS)
                .filter((status) => status !== "Junk")
                .map((status) => (
                  <Bar
                    key={status}
                    dataKey={status}
                    stackId="a"
                    fill={STATUS_COLORS[status]}
                    barSize={16}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        {/* Status chips */}
        <div className="bg-slate-800 rounded-xl p-6 col-span-2">
          <p className="text-white font-semibold mb-2">
            Leads Quality Distribution
          </p>
          <div className="flex flex-col gap-3 mt-5">
            {Object.entries(statusCount)
              .filter(([status]) => status !== "Junk") // remove Junk
              .map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <div
                    className={`inline-block w-16 text-center rounded px-3 py-1 font-medium text-xs shadow ${HIGHLIGHT[status]}`}
                  >
                    {status}
                  </div>
                  <div className="flex-1 mx-3 relative">
                    <div className="absolute bg-slate-700 h-2 w-full rounded" />
                    <div
                      className="h-2 rounded"
                      style={{
                        width: `${Math.min(
                          100,
                          (count / leads.length) * 100
                        )}%`,
                        background: STATUS_COLORS[status],
                        position: "relative",
                        zIndex: 1,
                      }}
                    />
                  </div>
                  <span className="ml-2 text-white text-sm">{count}</span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Stacked Bar & Status */}
      <div className="grid md:grid-cols-4 gap-7 mb-10">
        {/* Bar Chart */}
        <div className="bg-slate-800 rounded-xl p-6 col-span-2">
          <p className="font-semibold font-montserrat mb-4 text-white">
            Source-wise Lead Quality (%)
          </p>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barDataPercent}>
              <XAxis dataKey="source" stroke="#fff" />
              <YAxis stroke="#fff" />
              <Tooltip content={<CustomTooltip isPercentage={true} />} />
              <Legend />
              {Object.keys(STATUS_COLORS)
                .filter((status) => status !== "Junk")
                .map((status) => (
                  <Bar
                    key={status}
                    dataKey={status}
                    className="font-montserrat"
                    stackId="a"
                    fill={STATUS_COLORS[status]}
                    barSize={16}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        {/* Status chips */}
        {/* Existing progress bars, rewritten with % */}
        <div className="bg-slate-800 rounded-xl p-6 col-span-2">
          <p className="text-white font-semibold mb-2">
            Leads Quality Distribution (%)
          </p>
          <div className="flex flex-col gap-3 mt-5">
            {Object.entries(statusCount)
              .filter(([status]) => status !== "Junk") // remove Junk
              .map(([status, count]) => {
                const percent = Math.round((count / leads.length) * 100) || 0;
                return (
                  <div
                    key={status}
                    className="flex items-center justify-between"
                  >
                    <div
                      className={`inline-block w-16 text-center rounded px-3 py-1 font-medium text-xs shadow ${HIGHLIGHT[status]}`}
                    >
                      {status}
                    </div>
                    <div className="flex-1 mx-3 relative">
                      <div className="absolute bg-slate-700 h-2 w-full rounded" />
                      <div
                        className="h-2 rounded flex items-center justify-end text-xs"
                        style={{
                          width: `${percent}%`,
                          background: STATUS_COLORS[status],
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        {/* Show % inside bar if wide enough */}
                        {/* {percent > 15 && (
                        <span className="ml-2 text-white font-bold">
                          {percent}%
                        </span>
                      )} */}
                      </div>
                    </div>
                    {/* ✅ Only percentage shown here */}
                    <span className="ml-2 text-white text-sm">{percent}%</span>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
      {/* Source-wise Status Breakdown Table */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-8">
        {/* Table: Source-wise Status Breakdown (Counts) */}
        <div className="bg-slate-800 rounded-xl p-6">
          <h2 className="mb-4 font-semibold text-white">
            Source-wise Leads Breakdown (Count)
          </h2>

          <div className="overflow-x-auto rounded">
            <table className="min-w-full text-gray-200 text-sm">
              <thead>
                <tr className="bg-slate-700 text-white">
                  <th className="p-2">Source</th>
                  <th className="p-2">Hot</th>
                  <th className="p-2">Warm</th>
                  <th className="p-2">Cold</th>
                  <th className="p-2">Junk</th>
                </tr>
              </thead>
              <tbody>
                {barData.map((row) => (
                  <tr key={row.source} className="bg-slate-800">
                    <td className="p-2 capitalize text-center">{row.source}</td>
                    <td className="p-2 text-center">{row.Hot}</td>
                    <td className="p-2 text-center">{row.Warm}</td>
                    <td className="p-2 text-center">{row.Cold}</td>
                    <td className="p-2 text-center">{row.Junk}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* Table: Source-wise Status Breakdown (Percentages) */}
        <div className="bg-slate-800 rounded-xl p-6">
          <h2 className="mb-4 font-semibold text-white">
            Source-wise Leads Breakdown (%)
          </h2>
          <div className="overflow-x-auto rounded">
            <table className="min-w-full text-gray-200 text-sm">
              <thead>
                <tr className="bg-slate-700 text-white">
                  <th className="p-2">Source</th>
                  <th className="p-2">Hot (%)</th>
                  <th className="p-2">Warm (%)</th>
                  <th className="p-2">Cold (%)</th>
                  <th className="p-2">Junk (%)</th>
                </tr>
              </thead>
              <tbody>
                {barDataPercent.map((row) => (
                  <tr key={row.source} className="bg-slate-800">
                    <td className="p-2 capitalize text-center">{row.source}</td>
                    <td className="p-2 text-center">{row.Hot}%</td>
                    <td className="p-2 text-center">{row.Warm}%</td>
                    <td className="p-2 text-center">{row.Cold}%</td>
                    <td className="p-2 text-center">{row.Junk}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Best Source & Counts Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
        {/* Best Source Card */}
        <div className="bg-gradient-to-br from-indigo-500 via-indigo-600 to-slate-700 text-center rounded-xl py-6 px-4 shadow-lg flex flex-col justify-center items-center">
          <span className="text-md  text-white mb-2 ">Best Source</span>
          <span className="text-lg font-bold text-white">
            {bestSourceData?.bestSource}
          </span>
        </div>
        {/* Hot+Warm */}
        <div className="bg-gradient-to-br from-cyan-500 via-cyan-600 to-slate-700 text-center rounded-xl py-6 px-4 shadow-lg flex flex-col justify-center items-center">
          <span className="text-md  text-white mb-2">
            Count of Hot+Warm - {bestSourceData?.bestSource}
          </span>
          <span className="text-lg font-bold text-white">
            {bestSourceData?.bestHotWarm}
          </span>
        </div>
        {/* Test Drives Requested */}
        <div className="bg-gradient-to-br from-fuchsia-500 via-fuchsia-600 to-slate-700 text-center rounded-xl py-6 px-4 shadow-lg flex flex-col justify-center items-center">
          <span className="text-md  text-white mb-2">
            Test Drives Requested - {bestSourceData?.bestSource}
          </span>
          <span className="text-lg font-bold text-white">
            {bestSourceData?.bestTestDrive}
          </span>
        </div>
      </div>
      {/* Data Table */}
      <div className="bg-slate-800 rounded-xl p-6">
        <h2 className="mb-4 font-semibold text-white">Lead Data Table</h2>
        <div className="mb-4">
          <DownloadButton
            onClick={handleExport}
            title={"Export Digital Leads Data"}
          />
        </div>

        <div className="overflow-x-auto rounded">
          <table className="min-w-full text-gray-200 text-sm">
            <thead>
              <tr className="bg-purple-700 text-white font-bold">
                <th className="p-2">Name</th>
                <th className="p-2">Vehicle</th>
                <th className="p-2">Source</th>
                <th className="p-2">Status</th>
                <th className="p-2">Phone No.</th>

                <th className="p-2">Test Drive?</th>
                <th className="p-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {leads.slice(page * 15, page * 15 + 15).map((l, i) => (
                <DigitalLeadRow
                  onAddRemarkClick={() => editLeadRemark(l)}
                  lead={l}
                  key={i}
                  index={i}
                />
              ))}
            </tbody>
          </table>
          <TablePagination
            rowsPerPageOptions={[]}
            sx={{
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-input":
                {
                  fontSize: "14px",
                  color: "white",
                },
              "& .MuiTablePagination-actions button": { color: "#fff" },
              "& .MuiTablePagination-displayedRows": {
                fontSize: "14px",
                color: "white", // Change color
                fontWeight: "bold",
              },
            }}
            component="div"
            count={leads.length}
            rowsPerPage={15}
            page={page}
            onPageChange={(e, page) => setPage(page)}
          />
        </div>
      </div>
      {/* Leads Over Time & Test Drive Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-8">
        {/* Line Chart: Leads Over Time */}
        <div className="bg-slate-800 rounded-xl p-6">
          <h2 className="mb-4 font-semibold text-white">Leads Over Time</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={weekData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" stroke="#fff" />
              <YAxis stroke="#fff" />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="leads"
                stroke="#3b82f6"
                strokeWidth={3}
                dot={{ stroke: "#3b82f6", strokeWidth: 2, fill: "#fff" }}
                name="Total Leads"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Stacked Bar Chart: Test Drive Interested / Booked by Source */}
        <div className="bg-slate-800 rounded-xl p-6">
          <h2 className="mb-4 font-semibold text-white">
            Test Drive: Interested vs Booked (by Source)
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={testDriveBarData}>
              <XAxis dataKey="source" stroke="#fff" />
              <YAxis stroke="#fff" />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="Interested"
                stackId="a"
                fill="#4682B4"
                barSize={24}
                name="Test Drive Interested"
              />
              <Bar
                dataKey="Booked"
                stackId="a"
                fill="#5EDFFF"
                barSize={24}
                name="Test Drive Booked"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <Modal
        className="max-w-[700px]"
        isOpen={remarkModal}
        onClose={() => closeModal()}
      >
        <div className="no-scrollbar font-montserrat relative w-full max-w-[700px] overflow-y-auto rounded-3xl bg-white p-4 dark:bg-gray-900 lg:p-11">
          <div className="px-2 pr-14">
            <h4 className="mb-5 text-xl font-semibold text-gray-800 dark:text-white/90">
              Add Remark
            </h4>
          </div>
          <textarea
            type="text"
            rows={5}
            name="remark"
            className={`block w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-blue-400 transition
                ${errors.remark ? "border-red-400" : " border-gray-700"}
              bg-gray-900  text-gray-100
              `}
            onChange={handleChange}
            placeholder="Enter Remark"
          />
          <small className="text-red-500 text-sm">{errors.remark}</small>
          <div className="flex flex-col">
            <div className="flex items-center gap-3 px-2 mt-6 lg:justify-end">
              <button
                className="bg-red-400 text-white text-sm py-2 px-4 rounded-lg"
                onClick={() => closeModal()}
              >
                Close
              </button>
              <button
                disabled={buttonLoading}
                className="text-white text-sm bg-purple-700 hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg px-2 py-2 dark:bg-purple-600 dark:hover:bg-purple-700 focus:outline-none dark:focus:ring-purple-800"
                onClick={handleSubmit}
              >
                {buttonLoading ? "Saving ..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
